import { forwardRef, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import {
  formatPoDateValue,
  formatPoMoney,
  formatPoNumberValue,
  formatPoPercent,
} from '@/lib/poTemplate/format'
import { placeholderValues, resolvePlaceholders, type PlaceholderValues } from '@/lib/poTemplate/placeholders'
import {
  accentColor,
  columnValue,
  fieldLabel,
  fieldVisible,
  isNumericColumn,
  sectionRows,
  type MaterialAttributes,
} from '@/lib/poTemplate/layout'
import type {
  PoTemplateLayout,
  PoTemplateSection,
  PurchaseOrderAddress,
  PurchaseOrderDetail,
} from '@/types/purchaseOrders'
import { PoMarkdown } from './PoMarkdown'

export interface PoDocumentOptions {
  /** Object URL of the logo (see usePoLogo); null prints without one. */
  logoUrl?: string | null
  /** Printed diagonally across the page — drafts are not orders yet. */
  watermark?: 'DRAFT'
  /** `preview` draws a sheet of paper on screen; `print` leaves margins to @page. */
  mode: 'preview' | 'print'
  /** Line material attributes by material id, for `attr:` columns. */
  attributes?: MaterialAttributes
}

interface PoDocumentProps extends PoDocumentOptions {
  layout: PoTemplateLayout
  po: PurchaseOrderDetail
}

/** Sheet sizes, for the on-screen preview. Print takes the size from @page. */
export const PO_PAPER = {
  A4: { width: '210mm', minHeight: '297mm', css: 'A4', widthMm: 210 },
  Letter: { width: '215.9mm', minHeight: '279.4mm', css: 'letter', widthMm: 215.9 },
} as const

/** One sans stack: no web font is loaded in this app, so the system UI face. */
const FONT = "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

/** Terms longer than this set in two columns, as on the back of a paper PO. */
const TERMS_TWO_COLUMNS_AT = 900

/**
 * A purchase order on paper (z8nrz7f7dz) — THE renderer. The template
 * editor's live preview, the Print view and Download PDF all draw this one
 * component, so what a planner designs is what the supplier receives.
 *
 * Laid out as a classic PO: a header band (identity left, title and order
 * facts right, an accent rule beneath), the vendor / ship-to / bill-to
 * columns, an order-terms strip, the lines, notes beside totals, the
 * authorization lines, terms in small type, and a footer. Neutral slate with
 * ONE accent colour from the template; hairline rules, no boxes.
 *
 * Every section, label, column and text is the template's own: sections print
 * in its order and only when visible, through the placeholder allow-list and
 * the safe markdown renderer — nothing is evaluated, nothing reaches the DOM
 * as markup.
 */
export const PoDocument = forwardRef<HTMLDivElement, PoDocumentProps>(function PoDocument(
  { layout, po, logoUrl, watermark, mode, attributes },
  ref,
) {
  const paper = PO_PAPER[layout.document.paperSize] ?? PO_PAPER.A4
  const accent = accentColor(layout)
  const values = placeholderValues(layout, po)
  const ctx: Ctx = { layout, po, values, logoUrl: logoUrl ?? null, attributes: attributes ?? {} }
  const style = {
    '--po-accent': accent,
    fontFamily: FONT,
    fontVariantNumeric: 'tabular-nums',
    ...(mode === 'preview' ? { width: paper.width, minHeight: paper.minHeight, padding: '15mm' } : {}),
  } as CSSProperties

  return (
    <div
      ref={ref}
      className={cn(
        'po-document relative bg-white text-[9pt] leading-[1.45] text-slate-800',
        mode === 'preview' && 'mx-auto shadow-[0_1px_3px_rgba(15,23,42,0.12),0_8px_24px_rgba(15,23,42,0.06)]',
      )}
      style={style}
      data-testid='po-document'
    >
      {/* Print rules live with the document so every surface that prints it
          gets them. "x / y" uses the @page margin boxes Chrome supports; a
          browser without them simply prints no page number. */}
      <style>{`
        @page { size: ${paper.css}; margin: 15mm 15mm 18mm 15mm;
          @bottom-right { content: counter(page) " / " counter(pages); font: 7.5pt ${FONT}; color: #64748b; } }
        @media print {
          html, body { background: #fff !important; }
          .po-document { box-shadow: none !important; }
          .po-no-print { display: none !important; }
        }
        .po-document thead { display: table-header-group; }
        .po-document tr, .po-document .po-keep { break-inside: avoid; page-break-inside: avoid; }
      `}</style>

      {watermark && (
        <div aria-hidden className='pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden'>
          <span className='-rotate-[35deg] select-none text-[110pt] font-bold tracking-[0.2em] text-slate-900/[0.045]'>
            {watermark}
          </span>
        </div>
      )}

      <div className='relative flex flex-col gap-[7mm]'>
        {sectionRows(layout.sections, !!po.notes?.trim()).map((row, i) => {
          if (row.kind === 'addresses') {
            return (
              <div
                key={i}
                className='po-keep grid gap-[8mm]'
                style={{ gridTemplateColumns: `repeat(${row.sections.length}, minmax(0, 1fr))` }}
              >
                {row.sections.map((s) => (
                  <Address key={s.key} section={s} ctx={ctx} />
                ))}
              </div>
            )
          }
          if (row.kind === 'summary') {
            return (
              <div key={i} className='po-keep grid grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] items-start gap-[10mm]'>
                <div>{row.notes && <Notes section={row.notes} ctx={ctx} />}</div>
                <Totals section={row.totals} ctx={ctx} />
              </div>
            )
          }
          return <Section key={i} section={row.section} ctx={ctx} />
        })}
      </div>
    </div>
  )
})

interface Ctx {
  layout: PoTemplateLayout
  po: PurchaseOrderDetail
  values: PlaceholderValues
  logoUrl: string | null
  attributes: MaterialAttributes
}

function Section({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  switch (section.key) {
    case 'header':
      return <Header section={section} ctx={ctx} />
    case 'billTo':
    case 'shipTo':
    case 'supplier':
      return <Address section={section} ctx={ctx} />
    case 'orderTerms':
      return <OrderTerms section={section} ctx={ctx} />
    case 'lines':
      return <Lines ctx={ctx} />
    case 'totals':
      return <Totals section={section} ctx={ctx} />
    case 'notes':
      return <Notes section={section} ctx={ctx} />
    case 'terms':
      return <Terms section={section} ctx={ctx} />
    case 'authorization':
      return <Authorization section={section} />
    case 'footer':
      return <Footer ctx={ctx} />
    default:
      return null
  }
}

/** The small-caps accent heading every block carries. */
function Heading({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2
      className={cn('mb-[1.5mm] text-[7pt] font-semibold uppercase tracking-[0.12em]', className)}
      style={{ color: 'var(--po-accent)' }}
    >
      {children}
    </h2>
  )
}

const present = (v: string | null | undefined): v is string => !!v && v.trim() !== ''

// ─── 1. Header band ─────────────────────────────────────────────────────────

function Header({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  const { layout, po, logoUrl } = ctx
  const { branding, company, document } = layout
  const locale = document.locale
  const logoRight = branding.logoPosition === 'right'
  const a = company.address ?? {}

  const meta: [key: string, label: string, value: string | null][] = [
    ['number', 'PO Number', po.number],
    ['date', 'Date', formatPoDateValue(po.sentAt ?? po.createdAt, locale)],
    ['revision', 'Revision', po.revision > 0 ? String(po.revision) : null],
    ['requestedDeliveryDate', 'Requested Delivery', po.requestedDeliveryDate ? formatPoDateValue(po.requestedDeliveryDate, locale) : null],
  ]

  const identity = (
    <div className={cn('min-w-0 space-y-[2mm]', logoRight && 'text-right')}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={company.legalName ?? 'Logo'}
          style={{ height: `${branding.logoHeightMm}mm` }}
          className={cn('max-w-[70mm] object-contain', logoRight ? 'ml-auto' : 'object-left')}
        />
      )}
      <div className='space-y-[0.4mm] text-[7.5pt] leading-[1.5] text-slate-600'>
        {present(company.legalName) && <p className='text-[10pt] font-semibold text-slate-900'>{company.legalName}</p>}
        {present(a.street) && <p>{a.street}</p>}
        {(present(a.postcode) || present(a.city)) && <p>{[a.postcode, a.city].filter(present).join(' ')}</p>}
        {present(a.country) && <p>{a.country}</p>}
        {(present(company.phone) || present(company.email)) && (
          <p>{[company.phone, company.email].filter(present).join('  ·  ')}</p>
        )}
        {present(company.taxId) && <p>VAT {company.taxId}</p>}
      </div>
    </div>
  )

  const title = (
    <div className={cn('min-w-0', !logoRight && 'text-right')}>
      <div className={cn('flex items-center gap-[2.5mm]', !logoRight && 'justify-end')}>
        <h1 className='text-[19pt] font-semibold uppercase leading-none tracking-[0.06em]' style={{ color: 'var(--po-accent)' }}>
          {document.title}
        </h1>
        {po.revision > 0 && (
          <span
            className='rounded-full border px-[2mm] py-[0.4mm] text-[7pt] font-semibold uppercase tracking-wider'
            style={{ color: 'var(--po-accent)', borderColor: 'var(--po-accent)' }}
          >
            Rev {po.revision}
          </span>
        )}
      </div>
      <table className={cn('mt-[4mm] text-[8pt]', !logoRight && 'ml-auto')}>
        <tbody>
          {meta
            .filter(([key, , value]) => value !== null && fieldVisible(section, key))
            .map(([key, label, value]) => (
              <tr key={key}>
                <td className='py-[0.6mm] pr-[5mm] text-left text-slate-500'>{fieldLabel(section, key, label)}</td>
                <td className={cn('py-[0.6mm] text-right', key === 'number' ? 'font-semibold text-slate-900' : 'text-slate-800')}>
                  {value}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <header className='po-keep border-b-[1.5pt] pb-[5mm]' style={{ borderColor: 'var(--po-accent)' }}>
      <div className='grid grid-cols-2 items-start gap-[10mm]'>
        {logoRight ? title : identity}
        {logoRight ? identity : title}
      </div>
    </header>
  )
}

// ─── 2. Address columns ─────────────────────────────────────────────────────

function Address({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  const rows = addressRows(section, ctx)
  return (
    <section className='min-w-0'>
      <Heading>{section.title}</Heading>
      <div className='space-y-[0.4mm] text-[8.5pt] leading-[1.5]'>
        {rows
          .filter(([key, , value]) => present(value) && fieldVisible(section, key))
          .map(([key, , value]) => (
            <p key={key} className={key === 'name' ? 'font-semibold text-slate-900' : 'text-slate-700'}>
              {value}
            </p>
          ))}
      </div>
    </section>
  )
}

function addressRows(section: PoTemplateSection, { layout, po }: Ctx): [string, string, string | null | undefined][] {
  const cityLine = (postcode?: string | null, city?: string | null) => [postcode, city].filter(present).join(' ')
  if (section.key === 'supplier') {
    return [
      ['name', 'Name', po.supplierName],
      ['contact', 'Contact', po.supplierContact ? `Attn: ${po.supplierContact}` : null],
      ['address', 'Address', po.supplierAddress],
      ['phone', 'Phone', po.supplierPhone],
      ['email', 'Email', po.supplierEmail],
      ['taxId', 'VAT', po.supplierTaxId ? `VAT ${po.supplierTaxId}` : null],
    ]
  }
  if (section.key === 'shipTo') {
    // The order's own ship-to wins; the template's is the default address.
    const own = po.shipTo
    const def = layout.shipTo
    const pick = (k: keyof PurchaseOrderAddress) => own?.[k] || def[k]
    return [
      ['name', 'Name', pick('name')],
      ['street', 'Street', pick('street')],
      ['city', 'City', cityLine(pick('postcode'), pick('city'))],
      ['country', 'Country', pick('country')],
      ['contact', 'Contact', def.contact ? `Attn: ${def.contact}` : null],
      ['phone', 'Phone', pick('phone')],
      ['email', 'Email', pick('email')],
    ]
  }
  const c = layout.company
  const a = c.address ?? {}
  return [
    ['name', 'Name', c.legalName],
    ['street', 'Street', a.street],
    ['city', 'City', cityLine(a.postcode, a.city)],
    ['country', 'Country', a.country],
    ['email', 'Email', c.email],
    ['taxId', 'VAT', c.taxId ? `VAT ${c.taxId}` : null],
  ]
}

// ─── 3. Order terms strip ───────────────────────────────────────────────────

function OrderTerms({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  const { po, layout } = ctx
  const locale = layout.document.locale
  const custom = layout.customFields.map((f): [string, string, string | null] => {
    const v = po.customFields?.[f.key]
    const text =
      v == null || v === ''
        ? null
        : f.type === 'date'
          ? formatPoDateValue(String(v), locale)
          : f.type === 'number'
            ? formatPoNumberValue(Number(v), locale)
            : String(v)
    return [`custom:${f.key}`, f.label, text]
  })
  const cells = (
    [
      ['buyer', 'Buyer', po.buyer],
      ['shippingMethod', 'Ship Via', po.shippingMethod],
      ['incoterms', 'Incoterms', po.incoterms],
      ['paymentTerms', 'Payment Terms', po.paymentTerms],
      ['currency', 'Currency', po.currency],
      ['reference', 'Reference', po.reference],
      ...custom,
    ] as [string, string, string | null][]
  ).filter(([key, , value]) => present(value) && fieldVisible(section, key))
  if (cells.length === 0) return null
  const perRow = Math.min(cells.length, 6)

  return (
    <section className='po-keep'>
      <div
        className='grid border-y border-slate-300'
        style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
      >
        {cells.map(([key, label, value], i) => (
          <div key={key} className={cn('px-[2.5mm] py-[2mm]', i % perRow !== 0 && 'border-l border-slate-200')}>
            <p className='text-[6.5pt] font-medium uppercase tracking-[0.1em] text-slate-500'>{fieldLabel(section, key, label)}</p>
            <p className='mt-[0.5mm] truncate text-[8.5pt] text-slate-900'>{value}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── 4. Line items ──────────────────────────────────────────────────────────

function Lines({ ctx }: { ctx: Ctx }) {
  const { layout, po, attributes } = ctx
  const cols = layout.columns.filter((c) => c.visible)
  return (
    <section>
      <table className='w-full border-collapse text-[8.5pt]'>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'border-b-[1.25pt] px-[1.8mm] pb-[1.5mm] pt-0 text-[7pt] font-semibold uppercase tracking-[0.08em] text-slate-600',
                  isNumericColumn(c.key) ? 'text-right' : 'text-left',
                  c.key === 'lineNo' && 'w-[8mm]',
                )}
                style={{ borderColor: 'var(--po-accent)' }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {po.lines.map((l, i) => (
            <tr key={l.id} className={cn('border-b border-slate-200', i % 2 === 1 && 'bg-[#f8fafc]')}>
              {cols.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-[1.8mm] py-[1.8mm] align-top',
                    isNumericColumn(c.key) && 'whitespace-nowrap text-right',
                    c.key === 'lineNo' && 'text-slate-500',
                    c.key === 'lineTotal' && 'font-medium text-slate-900',
                  )}
                >
                  {columnValue(c, l, po, layout.document.locale, attributes)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

// ─── 5. Notes + totals ──────────────────────────────────────────────────────

function Notes({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  if (!ctx.po.notes?.trim()) return null
  return (
    <section className='po-keep border border-slate-200 px-[3.5mm] py-[3mm]'>
      <Heading>{section.title}</Heading>
      <PoMarkdown text={resolvePlaceholders(ctx.po.notes, ctx.values)} className='text-[8.5pt] text-slate-700' />
    </section>
  )
}

function Totals({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  const { po, layout } = ctx
  const { locale } = layout.document
  const money = (n: number) => formatPoMoney(n, po.currency, locale)
  const rate = po.taxRatePct ?? layout.document.defaults.taxRatePct
  const rows: { key: string; label: string; value: string; shown: boolean }[] = [
    { key: 'subtotal', label: 'Subtotal', value: money(po.totals.subtotal), shown: true },
    { key: 'discountTotal', label: 'Discount', value: `− ${money(po.totals.discountTotal)}`, shown: po.totals.discountTotal > 0 },
    { key: 'shipping', label: 'Shipping', value: money(po.totals.shipping), shown: po.totals.shipping > 0 },
    { key: 'tax', label: `${fieldLabel(section, 'tax', 'VAT')} ${formatPoPercent(rate, locale)}`, value: money(po.totals.tax), shown: true },
  ]
  const totalLabel = fieldLabel(section, 'grandTotal', 'Total')
  return (
    <section className='po-keep'>
      <table className='w-full text-[8.5pt]'>
        <tbody>
          {rows
            .filter((r) => r.shown && fieldVisible(section, r.key))
            .map((r) => (
              <tr key={r.key}>
                <td className='py-[1mm] text-slate-600'>{r.key === 'tax' ? r.label : fieldLabel(section, r.key, r.label)}</td>
                <td className='py-[1mm] text-right text-slate-800'>{r.value}</td>
              </tr>
            ))}
          {fieldVisible(section, 'grandTotal') && (
            <tr className='border-t-[1.25pt]' style={{ borderColor: 'var(--po-accent)' }}>
              <td className='pt-[2mm] text-[9.5pt] font-bold uppercase tracking-[0.06em] text-slate-900'>
                {totalLabel} <span className='font-semibold text-slate-500'>{po.currency}</span>
              </td>
              <td className='pt-[2mm] text-right text-[11pt] font-bold text-slate-900'>{money(po.totals.grandTotal)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  )
}

// ─── 6. Authorization ───────────────────────────────────────────────────────

/** Signature lines when the template names none of its own. */
const DEFAULT_AUTHORIZATION_FIELDS = [
  { key: 'approvedBy', label: 'Authorized by', visible: true },
  { key: 'name', label: 'Name', visible: true },
  { key: 'date', label: 'Date', visible: true },
]

/** One blank, labelled line per visible field — signed on paper, in the template's order. */
function Authorization({ section }: { section: PoTemplateSection }) {
  const lines = (section.fields?.length ? section.fields : DEFAULT_AUTHORIZATION_FIELDS).filter((f) => f.visible)
  if (lines.length === 0) return null
  return (
    <section className='po-keep'>
      {section.title && <Heading>{section.title}</Heading>}
      <div className='grid gap-[8mm] pt-[9mm]' style={{ gridTemplateColumns: `repeat(${lines.length}, minmax(0, 1fr))` }}>
        {lines.map((f) => (
          <div key={f.key} className='border-t border-slate-400 pt-[1mm] text-[7pt] uppercase tracking-[0.08em] text-slate-500'>
            {f.label}
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── 7. Terms ───────────────────────────────────────────────────────────────

function Terms({ section, ctx }: { section: PoTemplateSection; ctx: Ctx }) {
  // The order's own terms when it has them, else the template's standard terms.
  const text = ctx.po.terms?.trim() ? ctx.po.terms : ctx.layout.textBlocks.terms
  if (!text?.trim()) return null
  return (
    <section>
      <Heading>{section.title}</Heading>
      <PoMarkdown
        text={resolvePlaceholders(text, ctx.values)}
        className={cn(
          'text-[7.5pt] leading-[1.45] text-slate-600 [&_h3]:text-[8.5pt] [&_h4]:text-[8pt] [&_h5]:text-[7.5pt] [&_h3]:text-slate-800 [&_h4]:text-slate-800 [&_h5]:text-slate-800',
          text.length > TERMS_TWO_COLUMNS_AT && 'columns-2 gap-[8mm] [&>*]:break-inside-avoid',
        )}
      />
    </section>
  )
}

// ─── 8. Footer ──────────────────────────────────────────────────────────────

function Footer({ ctx }: { ctx: Ctx }) {
  const text = ctx.layout.textBlocks.footer
  if (!text?.trim()) return null
  return (
    <footer className='po-keep border-t border-slate-200 pt-[2.5mm] text-[7pt] text-slate-500'>
      <PoMarkdown text={resolvePlaceholders(text, ctx.values)} className='[&_p]:mb-[0.5mm]' />
    </footer>
  )
}
